import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.dataset.currentChecked === 'true'
      );

      this.#checkedIndices.push(
        initialCheckedIndex === -1 ? [] : [initialCheckedIndex]
      );
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Clears the selected color option without requesting another variant.
   * Used when the customer manually returns to an unmapped gallery image.
   */
  clearColorSelection() {
    const isColorName = (value) => {
      const normalizedValue = String(value || '')
        .trim()
        .toLocaleLowerCase();

      return (
        normalizedValue.includes('farbe') ||
        normalizedValue.includes('color') ||
        normalizedValue.includes('colour')
      );
    };

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset.querySelectorAll('input'));
      const optionName = radios[0]?.dataset.optionName || '';

      if (!isColorName(optionName)) return;

      for (const radio of radios) {
        radio.checked = false;
        radio.defaultChecked = false;
        radio.removeAttribute('checked');
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }

      this.#radios[fieldsetIndex] = radios;
      this.#checkedIndices[fieldsetIndex] = [];
      fieldset.style.removeProperty('--pill-width-current');
      fieldset.style.removeProperty('--pill-width-previous');
    });

    for (const select of this.querySelectorAll('select')) {
      const optionName =
        select.selectedOptions[0]?.dataset.optionName ||
        select.options[0]?.dataset.optionName ||
        '';

      if (
        !isColorName(optionName) &&
        select.dataset.xscratchColorOption !== 'true'
      ) {
        continue;
      }

      for (const option of select.options) {
        option.removeAttribute('selected');
      }

      select.selectedIndex = -1;
    }
  }

  #readVariantOptionMap() {
    const script = this.querySelector(
      'script[data-xscratch-variant-option-map]'
    );

    if (!(script instanceof HTMLScriptElement)) return [];

    try {
      const variants = JSON.parse(script.textContent || '[]');

      if (!Array.isArray(variants)) return [];

      return variants.filter(
        (variant) =>
          variant &&
          (typeof variant.id === 'string' ||
            typeof variant.id === 'number') &&
          Array.isArray(variant.options)
      );
    } catch (error) {
      console.warn('[variant-picker] Invalid variant option map', error);
      return [];
    }
  }

  #clearOptionSelection(optionPosition) {
    const fieldset = Array.from(
      this.querySelectorAll('fieldset[data-option-position]')
    ).find(
      (item) =>
        Number.parseInt(item.dataset.optionPosition || '') ===
        optionPosition
    );

    if (fieldset instanceof HTMLFieldSetElement) {
      const radios = Array.from(
        fieldset.querySelectorAll('input[type="radio"]')
      );

      for (const radio of radios) {
        radio.checked = false;
        radio.defaultChecked = false;
        radio.removeAttribute('checked');
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }

      const fieldsetIndex = Number.parseInt(
        fieldset.dataset.fieldsetIndex || ''
      );

      if (!Number.isNaN(fieldsetIndex)) {
        this.#radios[fieldsetIndex] = radios;
        this.#checkedIndices[fieldsetIndex] = [];
      }

      fieldset.style.removeProperty('--pill-width-current');
      fieldset.style.removeProperty('--pill-width-previous');
    }

    const select = Array.from(
      this.querySelectorAll('select[data-option-position]')
    ).find(
      (item) =>
        Number.parseInt(item.dataset.optionPosition || '') ===
        optionPosition
    );

    if (select instanceof HTMLSelectElement) {
      for (const option of select.options) {
        option.removeAttribute('selected');
      }

      select.selectedIndex = -1;
    }
  }

  #resolveSelectedVariant(variants) {
    const selectedValues = new Map();

    for (const input of this.querySelectorAll(
      'fieldset input:checked[data-option-position]'
    )) {
      if (!(input instanceof HTMLInputElement)) continue;

      const position = Number.parseInt(
        input.dataset.optionPosition || ''
      );

      if (!Number.isNaN(position)) {
        selectedValues.set(position, input.value);
      }
    }

    for (const select of this.querySelectorAll(
      'select[data-option-position]'
    )) {
      if (!(select instanceof HTMLSelectElement) || !select.value) {
        continue;
      }

      const position = Number.parseInt(
        select.dataset.optionPosition || ''
      );

      if (!Number.isNaN(position)) {
        selectedValues.set(position, select.value);
      }
    }

    const matchingVariants = variants.filter((variant) => {
      for (const [position, value] of selectedValues) {
        if (String(variant.options[position - 1] ?? '') !== value) {
          return false;
        }
      }

      return true;
    });

    if (matchingVariants.length !== 1) return undefined;

    return matchingVariants[0];
  }

  /**
   * Keeps the product form in sync with a locally resolved linked variant.
   * This is intentionally done before the section request finishes so the
   * quantity selection and add-to-cart state react in the same frame.
   *
   * @param {Document | Element} root
   * @param {{id: string | number, available?: boolean} | undefined} variant
   */
  #syncLinkedProductFormState(root, variant) {
    if (!variant || !root?.querySelectorAll) return;

    const variantId = String(variant.id);
    const isAvailable = variant.available !== false;
    const productId = this.dataset.productId || '';
    const allProductForms = Array.from(
      root.querySelectorAll('product-form-component')
    ).filter((form) => form instanceof HTMLElement);
    const matchingProductForms = productId
      ? allProductForms.filter(
          (form) => form.dataset.productId === productId
        )
      : [];
    const productForms = matchingProductForms.length
      ? matchingProductForms
      : allProductForms.length === 1
        ? allProductForms
        : [];

    for (const productForm of productForms) {
      for (const variantInput of productForm.querySelectorAll(
        'input[name="id"]'
      )) {
        if (!(variantInput instanceof HTMLInputElement)) continue;

        variantInput.value = variantId;
        variantInput.disabled = !isAvailable;
      }

      for (const button of productForm.querySelectorAll(
        'button[ref="addToCartButton"]'
      )) {
        if (!(button instanceof HTMLButtonElement)) continue;

        button.disabled = !isAvailable;
        button.toggleAttribute('aria-disabled', !isAvailable);
      }
    }
  }

  /**
   * Applies a linked variant to the product form currently shown with this
   * picker.
   *
   * @param {{id: string | number, available?: boolean} | undefined} variant
   */
  #syncCurrentLinkedProductFormState(variant) {
    const root = this.closest('.shopify-section, dialog') || document;

    this.#syncLinkedProductFormState(root, variant);
  }

  #autoSelectUniqueDependentOption(target) {
    if (
      !(
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement
      )
    ) {
      return undefined;
    }

    const sourcePosition = Number.parseInt(
      this.dataset.xscratchAutoSourcePosition || ''
    );
    const targetPosition = Number.parseInt(
      this.dataset.xscratchAutoTargetPosition || ''
    );
    const changedPosition = Number.parseInt(
      target.dataset.optionPosition || ''
    );

    if (
      Number.isNaN(sourcePosition) ||
      Number.isNaN(targetPosition) ||
      changedPosition !== sourcePosition ||
      sourcePosition === targetPosition
    ) {
      return undefined;
    }

    const variants = this.#readVariantOptionMap();

    if (!variants.length || !target.value) {
      this.#clearOptionSelection(targetPosition);
      return undefined;
    }

    const dependentValues = new Set();

    for (const variant of variants) {
      if (
        String(variant.options[sourcePosition - 1] ?? '') ===
        target.value
      ) {
        const dependentValue = String(
          variant.options[targetPosition - 1] ?? ''
        );

        if (dependentValue) dependentValues.add(dependentValue);
      }
    }

    if (dependentValues.size !== 1) {
      this.#clearOptionSelection(targetPosition);
      return undefined;
    }

    const [dependentValue] = dependentValues;
    const dependentInput = Array.from(
      this.querySelectorAll(
        'fieldset input[type="radio"][data-option-position]'
      )
    ).find(
      (input) =>
        input instanceof HTMLInputElement &&
        Number.parseInt(input.dataset.optionPosition || '') ===
          targetPosition &&
        input.value === dependentValue
    );

    if (dependentInput instanceof HTMLInputElement) {
      if (!dependentInput.checked) {
        this.updateSelectedOption(dependentInput);
      }

      return this.#resolveSelectedVariant(variants);
    }

    const dependentSelect = Array.from(
      this.querySelectorAll('select[data-option-position]')
    ).find(
      (select) =>
        select instanceof HTMLSelectElement &&
        Number.parseInt(select.dataset.optionPosition || '') ===
          targetPosition
    );

    if (dependentSelect instanceof HTMLSelectElement) {
      const matchingOption = Array.from(dependentSelect.options).find(
        (option) => option.value === dependentValue
      );

      if (matchingOption) {
        if (dependentSelect.value !== matchingOption.value) {
          dependentSelect.value = matchingOption.value;
          this.updateSelectedOption(dependentSelect);
        }

        return this.#resolveSelectedVariant(variants);
      }
    }

    const hasRenderedTargetControl = Boolean(
      this.querySelector(
        `[data-option-position="${targetPosition}"] input, select[data-option-position="${targetPosition}"]`
      )
    );

    if (!hasRenderedTargetControl) {
      return this.#resolveSelectedVariant(variants);
    }

    this.#clearOptionSelection(targetPosition);
    return undefined;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    const autoSelectedVariant =
      this.#autoSelectUniqueDependentOption(event.target);
    const autoSelectedVariantId = autoSelectedVariant
      ? String(autoSelectedVariant.id)
      : undefined;

    if (autoSelectedVariant) {
      this.#syncCurrentLinkedProductFormState(autoSelectedVariant);
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(
      this.closest('featured-product-information')
    );

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    const optionValueId = selectedOption.dataset.optionValueId ?? '';

    let requestUrl = this.buildRequestUrl(selectedOption);

    /*
     * The linked size/quantity picker starts intentionally without a
     * Shopify-selected variant. Once the dependent quantity has been
     * selected automatically, send that resolved variant together with the
     * option values. The local fallback below still protects the product
     * form if Liquid treats option_values as the authoritative selection.
     */
    if (autoSelectedVariantId) {
      const requestUrlWithVariant = new URL(
        requestUrl,
        window.location.origin
      );

      requestUrlWithVariant.searchParams.set(
        'variant',
        autoSelectedVariantId
      );
      requestUrl = requestUrlWithVariant.toString();
    }

    this.fetchUpdatedSection(
      requestUrl,
      morphElementSelector,
      optionValueId,
      autoSelectedVariant
    );

    const url = new URL(window.location.href);
    const variantId =
      autoSelectedVariantId || selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset.
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth:
        currentIndex !== undefined
          ? radios[currentIndex]?.parentElement?.offsetWidth
          : undefined,
      previousWidth:
        previousIndex !== undefined
          ? radios[previousIndex]?.parentElement?.offsetWidth
          : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset.
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({
    fieldset,
    currentWidth,
    previousWidth,
    currentIndex,
    previousIndex,
  }) {
    if (currentWidth) {
      fieldset.style.setProperty(
        '--pill-width-current',
        `${currentWidth}px`
      );
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty(
        '--pill-width-previous',
        `${previousWidth}px`
      );
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);

    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`
      );

      if (!targetElement) {
        throw new Error('Target element not found');
      }

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(
        target.dataset.fieldsetIndex || ''
      );
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (
        !Number.isNaN(fieldsetIndex) &&
        !Number.isNaN(inputIndex)
      ) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
          this.refs.fieldsets || []
        );
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (
            currentIndex !== undefined &&
            radios[currentIndex]
          ) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }

          if (
            previousIndex !== undefined &&
            radios[previousIndex]
          ) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(
            checkedIndices.length,
            2
          );

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (
            newCurrentIndex !== undefined &&
            radios[newCurrentIndex]
          ) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (
            newPreviousIndex !== undefined &&
            radios[newPreviousIndex]
          ) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }

      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue
      );

      if (!newSelectedOption) {
        throw new Error('Option not found');
      }

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) {
      params.push(`view=${viewParamValue}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(
        `option_values=${this.selectedOptionsValues.join(',')}`
      );
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(
          `option_values=${sourceSelectedOptionsValues.join(',')}`
        );
      } else {
        params.push(
          `option_values=${selectedOption.dataset.optionValueId}`
        );
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component':
        'section-rendering-product-card',
      'featured-product-information': this.closest(
        'featured-product-information'
      )?.id,
    };

    const closestSectionId =
      /** @type {keyof typeof SECTION_ID_MAP | undefined} */ (
        Object.keys(SECTION_ID_MAP).find((sectionId) =>
          this.closest(sectionId)
        )
      );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl
   * @param {string} [morphElementSelector]
   * @param {string} [optionValueId]
   * @param {{id: string | number, available?: boolean}} [resolvedVariant]
   */
  fetchUpdatedSection(
    requestUrl,
    morphElementSelector,
    optionValueId = '',
    resolvedVariant
  ) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, {
      signal: this.#abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

        html
          .querySelector('overflow-list[defer]')
          ?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(
          'variant-picker script[type="application/json"]'
        );
        const textContent =
          variantPickerJsonScript?.textContent;
        const variantData = textContent
          ? JSON.parse(textContent)
          : null;
        const effectiveVariantData =
          variantData && typeof variantData === 'object'
            ? variantData
            : resolvedVariant;

        if (resolvedVariant) {
          /*
           * Liquid can still render the linked picker as "not selected"
           * during the option_values request. Correct the response before
           * other product components morph it into the live page.
           */
          this.#syncLinkedProductFormState(html, resolvedVariant);

          if (variantPickerJsonScript && effectiveVariantData) {
            variantPickerJsonScript.textContent = JSON.stringify(
              effectiveVariantData
            );
          }
        }

        if (!textContent && !effectiveVariantData) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList &&
            overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;

            if (
              overflowListAfterMorph instanceof OverflowList
            ) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        const sourceId = this.selectedOptionId || optionValueId;

        if (sourceId || resolvedVariant) {
          if (
            effectiveVariantData &&
            typeof effectiveVariantData === 'object'
          ) {
            const productViewAttr =
              variantPickerJsonScript
                ?.closest('[view-event-payload]')
                ?.getAttribute('view-event-payload')
                ?.trim();

            deferredEventPromise.resolve({
              variant:
                (
                  productViewAttr &&
                  JSON.parse(productViewAttr)
                )?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId,
                resource: effectiveVariantData,
              },
            });

            if (resolvedVariant) {
              requestAnimationFrame(() => {
                this.#syncCurrentLinkedProductFormState(
                  resolvedVariant
                );
              });
            }

            return;
          }
        }

        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);

        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase()
    );

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId =
        newVariantPickerSource.dataset.productId;
      const newProductUrl =
        newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = {
          id: newProductId,
          url: newProductUrl,
        };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) {
          return undefined;
        }

        return node.dataset.key;
      },
    });

    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );

    const measurements = fieldsets
      .map((_, index) =>
        this.#getFieldsetMeasurements(index)
      )
      .filter((measurement) => measurement !== null);

    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml
   * @param {string} elementSelector
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(
        `No new element source found for ${elementSelector}`
      );
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked'
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all selected options.
   * @returns {{name: string, value: string}[]}
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];

      if (selected?.dataset?.optionName) {
        options.push({
          name: selected.dataset.optionName,
          value: selected.value,
        });
      }
    }

    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll(
      'fieldset input:checked'
    );

    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({
          name: input.dataset.optionName,
          value: input.value,
        });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined}
   */
  get selectedOptionId() {
    const { selectedOption } = this;

    if (!selectedOption) {
      return undefined;
    }

    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected option value IDs.
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    /** @type {HTMLElement[]} */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) {
        throw new Error('No option value ID found');
      }

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
